import React, { useEffect, useState } from 'react';
import { companyAPI } from '../services/api';
import { PageError, loadError } from '../components/OfflineNotice';

function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 999,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '14px', padding: '1.5rem',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1.25rem' }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: '0.83rem', color: 'var(--color-muted)' }}>{label}</span>
      <span style={{ fontSize: '0.83rem', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const EMPTY_CREATE = { business_id: '', name: '', sku: '', sale_price: '', initial_qty: '', initial_cost: '', reorder_level: '', is_service: false };

export default function Products() {
  const [products, setProducts]   = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [creating, setCreating]   = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    Promise.all([
      companyAPI.listAllProducts(),
      companyAPI.listBiz(),
    ]).then(([pr, br]) => {
      setProducts(pr.data.products || []);
      setBusinesses(br.data.businesses || []);
    }).catch((err) => setError(loadError(err, 'Failed to load products')))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const q = search.trim().toLowerCase();
  const filtered = products.filter(p =>
    !q ||
    (p.name || '').toLowerCase().includes(q) ||
    (p.sku || '').toLowerCase().includes(q) ||
    (p.business_name || '').toLowerCase().includes(q)
  );

  const grouped = filtered.reduce((acc, p) => {
    const key = p.business_id;
    if (!acc[key]) acc[key] = { name: p.business_name, currency: p.currency, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  const stockColor = (p) => {
    if (Number(p.reorder_level) > 0 && Number(p.stock_qty) <= Number(p.reorder_level)) return 'var(--color-warning)';
    if (Number(p.stock_qty) <= 0) return 'var(--color-danger)';
    return 'var(--color-success)';
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name:          p.name,
      sku:           p.sku || '',
      sale_price:    String(Number(p.sale_price) || ''),
      reorder_level: p.reorder_level ? String(Number(p.reorder_level)) : '',
      stock_qty:     p.is_service ? '' : String(Number(p.stock_qty) || 0),
      is_service:    Boolean(p.is_service),
    });
    setSaveError('');
  };

  const closeEdit = () => { setEditing(null); setForm({}); setSaveError(''); };

  const openCreate = () => {
    setCreateForm({ ...EMPTY_CREATE, business_id: businesses[0]?.id || '' });
    setCreateError('');
    setCreating(true);
  };
  const closeCreate = () => { setCreating(false); setCreateForm(EMPTY_CREATE); setCreateError(''); };

  const handleCreate = async () => {
    if (!createForm.business_id) { setCreateError('Select a business'); return; }
    if (!createForm.name.trim()) { setCreateError('Name is required'); return; }
    const price = parseFloat(createForm.sale_price);
    if (isNaN(price) || price < 0) { setCreateError('Enter a valid selling price'); return; }
    setSaving(true);
    setCreateError('');
    try {
      const biz = businesses.find(b => b.id === createForm.business_id);
      const payload = {
        business_id:   createForm.business_id,
        name:          createForm.name.trim(),
        sku:           createForm.sku.trim() || null,
        sale_price:    price,
        initial_qty:   createForm.is_service ? null : (createForm.initial_qty ? parseFloat(createForm.initial_qty) : null),
        initial_unit_cost: createForm.is_service ? null : (createForm.initial_cost ? parseFloat(createForm.initial_cost) : null),
        reorder_level: createForm.is_service ? null : (createForm.reorder_level ? parseFloat(createForm.reorder_level) : null),
        is_service:    createForm.is_service,
      };
      const res = await companyAPI.createProduct(payload);
      const p   = res.data.product;
      setProducts(prev => [...prev, {
        ...p,
        business_id:   biz.id,
        business_name: biz.name,
        currency:      biz.currency,
        avg_cost: (createForm.initial_qty && createForm.initial_cost ? parseFloat(createForm.initial_cost) : 0),
        sale_count: 0, total_qty_sold: 0, total_revenue: 0, profit_margin: null, suppliers: null,
      }]);
      closeCreate();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { setSaveError('Name is required'); return; }
    const price = parseFloat(form.sale_price);
    if (isNaN(price) || price < 0) { setSaveError('Enter a valid selling price'); return; }
    if (!form.is_service && form.stock_qty !== '' && (isNaN(parseFloat(form.stock_qty)) || parseFloat(form.stock_qty) < 0)) {
      setSaveError('Enter a valid stock quantity'); return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name:          form.name.trim(),
        sku:           form.sku.trim() || null,
        sale_price:    price,
        reorder_level: form.is_service ? null : (form.reorder_level ? parseFloat(form.reorder_level) : null),
        stock_qty:     form.is_service ? null : (form.stock_qty === '' ? null : parseFloat(form.stock_qty)),
        is_service:    form.is_service,
      };
      const res = await companyAPI.updateProduct(editing.id, payload);
      const saved = res.data?.product;
      setProducts(prev => prev.map(x => x.id === editing.id ? {
        ...x,
        ...payload,
        stock_qty: saved ? saved.stock_qty : x.stock_qty,
        profit_margin: x.avg_cost > 0 ? Number(((price - x.avg_cost) / price * 100).toFixed(1)) : null,
      } : x));
      closeEdit();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><PageError error={error} /></div>;

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Products</h1>
        <button className="btn-primary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }} onClick={openCreate}>
          + Add
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by name, SKU or business…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', marginBottom: '1.25rem' }}
      />

      {Object.values(grouped).length === 0 && (
        <p className="text-muted">No products found.</p>
      )}

      {Object.values(grouped).map(group => (
        <div key={group.name} style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
            {group.name}
          </div>

          {group.items.map(p => {
            const noStock  = Number(p.stock_qty) <= 0;
            const lowStock = Number(p.reorder_level) > 0 && Number(p.stock_qty) <= Number(p.reorder_level);
            return (
              <div
                key={p.id}
                className="card"
                onClick={() => openEdit(p)}
                style={{ marginBottom: '0.5rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {p.name}
                    {p.sku && <span style={{ fontSize: '0.68rem', color: 'var(--color-muted)', fontWeight: 400 }}>SKU: {p.sku}</span>}
                    {p.is_service && <span style={{ fontSize: '0.65rem', background: 'rgba(99,102,241,0.15)', color: 'var(--color-primary)', borderRadius: '4px', padding: '1px 5px', fontWeight: 600 }}>Service</span>}
                  </div>
                  {!p.is_service && (
                    <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', color: stockColor(p), fontWeight: 600 }}>
                        {noStock ? 'Out of stock' : lowStock ? `Low stock: ${Number(p.stock_qty).toFixed(2)}` : `Stock: ${Number(p.stock_qty).toFixed(2)}`}
                      </span>
                      {p.profit_margin !== null && (
                        <span style={{ fontSize: '0.75rem', color: p.profit_margin >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                          {p.profit_margin}% margin
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 }}>{group.currency} {fmt(p.sale_price)}</span>
              </div>
            );
          })}
        </div>
      ))}

      {editing && (() => {
        const group = Object.values(grouped).find(g => g.items.some(i => i.id === editing.id));
        const cur   = group?.currency || '';
        return (
          <Modal title={editing.name} onClose={closeEdit}>
            {/* Editable fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {/* Service toggle */}
              <div
                onClick={() => setForm(f => ({ ...f, is_service: !f.is_service }))}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', cursor: 'pointer' }}
              >
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Service / subscription</div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--color-muted)' }}>No stock tracking — qty = hours, jobs, seats or billing units</div>
                </div>
                <div style={{ width: 36, height: 20, borderRadius: 999, background: form.is_service ? 'var(--color-primary)' : 'rgba(255,255,255,0.15)', transition: 'background 0.2s', position: 'relative', flexShrink: 0, marginLeft: '0.75rem' }}>
                  <div style={{ position: 'absolute', top: 2, left: form.is_service ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Product name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Product name"
                  style={{ width: '100%' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>SKU / product code (optional)</label>
                <input
                  value={form.sku}
                  onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                  placeholder="e.g. HNY500"
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>An optional short code to find this product faster. Leave blank if unsure.</div>
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>{form.is_service ? 'Service price' : 'Selling price'} ({cur})</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.sale_price}
                  onChange={e => setForm(f => ({ ...f, sale_price: e.target.value }))}
                  placeholder="0.00"
                  style={{ width: '100%' }}
                />
              </div>
              {!form.is_service && (
                <div>
                  <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Restock threshold (optional)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.reorder_level}
                    onChange={e => setForm(f => ({ ...f, reorder_level: e.target.value }))}
                    placeholder="Alert when stock falls below…"
                    style={{ width: '100%' }}
                  />
                </div>
              )}
              {!form.is_service && (
                <div>
                  <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Stock on hand</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.stock_qty}
                    onChange={e => setForm(f => ({ ...f, stock_qty: e.target.value }))}
                    placeholder="Current quantity on hand"
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>Changing this records a stock adjustment. It won't affect purchase costs or profit.</div>
                </div>
              )}
            </div>

            {/* Read-only info */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Performance</div>
              {!form.is_service && Number(editing.avg_cost) > 0 && (
                <InfoRow label="Avg cost price" value={`${cur} ${fmt(editing.avg_cost)}`} />
              )}
              <InfoRow label="Transactions"  value={Number(editing.sale_count)} />
              <InfoRow label="Qty sold"      value={Number(editing.total_qty_sold).toFixed(2)} />
              <InfoRow label="Total revenue" value={`${cur} ${fmt(editing.total_revenue)}`} />
              {editing.suppliers && <InfoRow label="Suppliers" value={editing.suppliers} />}
            </div>

            {saveError && <p style={{ color: 'var(--color-danger)', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{saveError}</p>}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button onClick={closeEdit} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
                Cancel
              </button>
            </div>
          </Modal>
        );
      })()}

      {creating && (
        <Modal title="New product" onClose={closeCreate}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>

            {/* Business selector */}
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Business</label>
              <select
                value={createForm.business_id}
                onChange={e => setCreateForm(f => ({ ...f, business_id: e.target.value }))}
                style={{ width: '100%' }}
              >
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            {/* Service toggle */}
            <div
              onClick={() => setCreateForm(f => ({ ...f, is_service: !f.is_service }))}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', cursor: 'pointer' }}
            >
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Service / subscription</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-muted)' }}>No stock tracking</div>
              </div>
              <div style={{ width: 36, height: 20, borderRadius: 999, background: createForm.is_service ? 'var(--color-primary)' : 'rgba(255,255,255,0.15)', transition: 'background 0.2s', position: 'relative', flexShrink: 0, marginLeft: '0.75rem' }}>
                <div style={{ position: 'absolute', top: 2, left: createForm.is_service ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
            </div>

            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Product name</label>
              <input
                value={createForm.name}
                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Product name"
                style={{ width: '100%' }}
                autoFocus
              />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>SKU / product code (optional)</label>
              <input
                value={createForm.sku}
                onChange={e => setCreateForm(f => ({ ...f, sku: e.target.value }))}
                placeholder={createForm.is_service ? 'e.g. BASIC-M' : 'e.g. HNY500'}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>An optional short code to find this product faster. Leave blank if unsure.</div>
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>
                {createForm.is_service ? 'Service price' : 'Selling price'} ({businesses.find(b => b.id === createForm.business_id)?.currency || ''})
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={createForm.sale_price}
                onChange={e => setCreateForm(f => ({ ...f, sale_price: e.target.value }))}
                placeholder="0.00"
                style={{ width: '100%' }}
              />
            </div>
            {!createForm.is_service && (
              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Quantity in stock (optional)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={createForm.initial_qty}
                  onChange={e => setCreateForm(f => ({ ...f, initial_qty: e.target.value }))}
                  placeholder="How many you have on hand now"
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>Recorded as opening stock. You can restock later from Purchases.</div>
              </div>
            )}
            {!createForm.is_service && parseFloat(createForm.initial_qty) > 0 && (
              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Cost price per unit (optional) ({businesses.find(b => b.id === createForm.business_id)?.currency || ''})</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={createForm.initial_cost}
                  onChange={e => setCreateForm(f => ({ ...f, initial_cost: e.target.value }))}
                  placeholder="What each unit cost you"
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>Used to calculate profit. Leave blank if unknown.</div>
              </div>
            )}
            {!createForm.is_service && (
              <div>
                <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.3rem' }}>Restock threshold (optional)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={createForm.reorder_level}
                  onChange={e => setCreateForm(f => ({ ...f, reorder_level: e.target.value }))}
                  placeholder="Alert when stock falls below…"
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>

          {createError && <p style={{ color: 'var(--color-danger)', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{createError}</p>}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={handleCreate} disabled={saving}>
              {saving ? 'Saving…' : 'Create product'}
            </button>
            <button onClick={closeCreate} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
